/**
 * CostSavingsIndicator Component
 *
 * Displays cost savings information for multi-study batch evaluation.
 * Shows reused criteria count, estimated vs actual costs, and savings percentage.
 */

import { DollarSign, RefreshCw, TrendingDown, CheckCircle } from 'lucide-react';

export interface CostSavingsIndicatorProps {
  criteriaReused: number;
  criteriaAnalyzed: number;
  estimatedCost: number;
  actualCost?: number; // only available after completion
  costSaved: number;
}

export function CostSavingsIndicator({
  criteriaReused,
  criteriaAnalyzed,
  estimatedCost,
  actualCost,
  costSaved,
}: CostSavingsIndicatorProps) {
  const totalCriteria = criteriaReused + criteriaAnalyzed;
  const reusePercentage = totalCriteria > 0 ? Math.round((criteriaReused / totalCriteria) * 100) : 0;

  // Calculate savings percentage
  const potentialFullCost = estimatedCost + costSaved;
  const savingsPercentage =
    potentialFullCost > 0 ? Math.round((costSaved / potentialFullCost) * 100) : 0;

  // Format currency display
  const formatCost = (amount: number) => `$${amount.toFixed(2)}`;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <DollarSign size={18} className="text-primary-600 dark:text-primary-400" />
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Cost Optimization</h3>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Criteria reuse section */}
        <div className="flex items-start gap-3">
          <div className="p-2 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
            <RefreshCw size={16} className="text-primary-600 dark:text-primary-400" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Reusing{' '}
              <span className="font-semibold text-primary-700 dark:text-primary-400">{criteriaReused}</span> of{' '}
              <span className="font-semibold">{totalCriteria}</span> criteria from initial review
            </p>
            <div className="mt-2 w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
              <div
                className="h-full bg-primary-500 rounded-full transition-all duration-300"
                style={{ width: `${reusePercentage}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {criteriaAnalyzed > 0
                ? `${criteriaAnalyzed} criteria need fresh analysis`
                : 'All criteria can be reused'}
            </p>
          </div>
        </div>

        {/* Cost display section */}
        <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">Estimated cost</span>
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{formatCost(estimatedCost)}</span>
          </div>

          {actualCost !== undefined && (
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Actual cost</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{formatCost(actualCost)}</span>
            </div>
          )}

          {/* Savings highlight */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-400">Saved from reuse</span>
            <span className="text-sm font-semibold text-green-600 dark:text-green-400">{formatCost(costSaved)}</span>
          </div>
        </div>

        {/* Savings badge */}
        {savingsPercentage > 0 && (
          <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
              <TrendingDown size={16} />
              <span className="text-sm font-medium">Cost Savings</span>
            </div>
            <span
              className={`
                inline-flex items-center gap-1 px-2.5 py-1 text-sm font-semibold rounded-full
                ${
                  savingsPercentage >= 50
                    ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
                    : savingsPercentage >= 25
                      ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300'
                      : 'bg-lime-100 dark:bg-lime-900/40 text-lime-800 dark:text-lime-300'
                }
              `}
            >
              <CheckCircle size={14} />
              {savingsPercentage}% saved
            </span>
          </div>
        )}

        {/* Detailed breakdown for actual costs */}
        {actualCost !== undefined && (
          <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-start gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <CheckCircle size={16} className="text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-800 dark:text-green-300">Evaluation Complete</p>
                <p className="text-xs text-green-700 dark:text-green-300 mt-0.5">
                  Total cost: {formatCost(actualCost)}{' '}
                  {actualCost <= estimatedCost ? (
                    <span>(within estimate)</span>
                  ) : (
                    <span className="text-amber-700 dark:text-amber-300">
                      ({formatCost(actualCost - estimatedCost)} over estimate)
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CostSavingsIndicator;
